"use client";

import React from "react";
import { ShieldCheck, Gauge, Compass, Sliders, Activity } from "lucide-react";

export interface AtrParameterCard {
    title: string;
    description: string;
    items: string[];
    icon: "Gauge" | "Compass" | "Sliders" | "Activity";
    iconColor: string;
}

export interface AtrParameterGridBlockProps {
    id?: string;
    tag?: string;
    title?: string;
    description?: string;
    accuracyLabel?: string;
    accuracyDesc?: string;
    cards?: AtrParameterCard[];
}

export default function AtrParameterGridBlock({
    id = "monitored-parameters",
    tag = "// Diagnostic Coverage",
    title = "Precision-aligned flight variables",
    description = "The system focuses extensively on Flight Data Recorder (FDR) parameters and turboprop engine data values, reconstructing cockpit warning logic and surface controls with absolute alignment.",
    accuracyLabel = "System Ingestion Accuracy",
    accuracyDesc = "Aerodynamic equations and engine models are cross-referenced with manufacturer performance guides to guarantee flight path reconstruction tolerances.",
    cards = [
        {
            title: "Engine Performance Data",
            description: "Continuous measurement of turboprop mechanics. Monitored values include:",
            items: [
                "Left & Right Engine Torque (%)",
                "Propeller Speeds (Np %)",
                "Interstage Turbine Temperature (ITT °C)",
                "Fuel Flow rates (FF kg/h)"
            ],
            icon: "Gauge",
            iconColor: "#00FF66"
        },
        {
            title: "Avionics & Flight Attitude",
            description: "Tracks dynamic orientations and flight state vectors. Core readings:",
            items: [
                "Pitch, Roll, and Yaw Attitude Angles",
                "Compass Heading & Drift factors",
                "Indicated Airspeed (IAS) • Altitude",
                "Vertical Velocities & Lateral G-Forces"
            ],
            icon: "Compass",
            iconColor: "#00E5FF"
        },
        {
            title: "Control Surface Kinematics",
            description: "Correlates cockpit control column inputs with surface deflections:",
            items: [
                "Control Column Pitch & Roll Positions",
                "Rudder Pedal displacements",
                "Aileron, Elevator, & Rudder Angles",
                "Flap, Slat, and Spoiler positions"
            ],
            icon: "Sliders",
            iconColor: "#FFB800"
        },
        {
            title: "Systems & Warning Flags",
            description: "Records flight system states and emergency triggers:",
            items: [
                "Landing Gear extension state",
                "Autopilot engagement registers",
                "Master Caution & Engine warning signals",
                "Pneumatic & Hydraulic circuit flags"
            ],
            icon: "Activity",
            iconColor: "rgba(255, 255, 255, 0.6)"
        }
    ]
}: AtrParameterGridBlockProps) {
    const renderIcon = (icon: string, color: string) => {
        const props = { className: "w-4 h-4", style: { color } };
        switch (icon) {
            case "Gauge": return <Gauge {...props} />;
            case "Compass": return <Compass {...props} />;
            case "Sliders": return <Sliders {...props} />;
            case "Activity": return <Activity {...props} />;
            default: return <Gauge {...props} />;
        }
    };

    return (
        <section id={id} className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0B0C] border-t border-white/[0.04] w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    
                    {/* Left Side: Summary text */}
                    <div className="lg:col-span-4 space-y-6">
                        {tag && (
                            <span className="text-xs font-mono uppercase tracking-widest text-[#00E5FF] block">
                                {tag}
                            </span>
                        )}
                        <h2 className="font-cormorant font-serif text-4xl md:text-5xl font-light text-white leading-tight">
                            {title}
                        </h2>
                        {description && (
                            <p className="text-sm text-white/50 leading-relaxed font-light">
                                {description}
                            </p>
                        )}
                        
                        {(accuracyLabel || accuracyDesc) && (
                            <div className="bg-[#111214] p-5 rounded-lg border border-white/[0.05] space-y-3">
                                <div className="flex items-center gap-2 text-xs font-mono text-[#00FF66]">
                                    <ShieldCheck className="w-4 h-4" /> {accuracyLabel}
                                </div>
                                <p className="text-[11px] text-white/40 leading-relaxed">
                                    {accuracyDesc}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Right Side: Parameter cards list layout */}
                    <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {cards.map((card, idx) => (
                            <div 
                                key={idx} 
                                className="bg-[#111214]/50 border border-white/[0.04] p-6 rounded-xl space-y-4"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white/[0.02] border border-white/10 rounded-md flex items-center justify-center">
                                        {renderIcon(card.icon, card.iconColor)}
                                    </div>
                                    <h4 className="text-sm font-semibold uppercase tracking-wider text-white">
                                        {card.title}
                                    </h4>
                                </div>
                                <p className="text-xs text-white/50 leading-relaxed font-light">
                                    {card.description}
                                </p>
                                {card.items && card.items.length > 0 && (
                                    <ul className="text-xs font-mono text-white/70 space-y-1.5 pl-2 list-inside list-disc">
                                        {card.items.map((item, itemIdx) => (
                                            <li key={itemIdx}>{item}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </div>

                </div>
            </div>
        </section>
    );
}
